import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismIcon } from '@mylighthouse/prism-ember';
import dayjs from 'dayjs';
import type Highlight from 'lumen/models/roi-trophies/highlight';
import { HIGHLIGHT_PERIOD_TYPE } from 'lumen/models/roi-trophies/highlight';
import type { HoteldateTransformReturn } from 'lumen/transforms/hoteldate';
import { PRIMA_VIEWS } from 'lumen/utils/constants/pricing-assistant-constants';

interface GeneralRoiTrophiesCalendarDateLinkSignature {
  Args: {
    highlight: Highlight;
  };
}

export default class GeneralRoiTrophiesCalendarDateLink extends Component<GeneralRoiTrophiesCalendarDateLinkSignature> {
  @service declare dates: Services['dates'];

  @service declare roiTrophies: Services['roi-trophies'];

  @service declare router: Services['router'];

  get formattedDate(): string {
    const { periodType } = this.args.highlight;
    const { partialDateFormat } = this.dates;

    if (periodType === HIGHLIGHT_PERIOD_TYPE.MONTH) {
      return dayjs(this.periodStart).format(
        partialDateFormat.YEAR_AND_MONTH_LONG,
      );
    }

    if (periodType === HIGHLIGHT_PERIOD_TYPE.WEEK) {
      return dayjs(this.periodStart).format(
        partialDateFormat.MONTH_AND_DAY_SHORT,
      );
    }

    return dayjs(this.periodStart).format(
      partialDateFormat.WEEKDAY_AND_YEAR_MONTH_AND_DAY_SHORT,
    );
  }

  get month(): string {
    return dayjs(this.periodStart).format('YYYY-MM');
  }

  private get periodStart(): HoteldateTransformReturn {
    return this.args.highlight.periodStart!;
  }

  get url(): string {
    return this.router.urlFor('hotel.pricing-manager', {
      queryParams: { month: this.month, view: PRIMA_VIEWS.CALENDAR },
    });
  }

  @action
  navigate(event: MouseEvent): void {
    if (event.metaKey || event.ctrlKey || event.shiftKey) {
      return; // let the browser handle the href naturally
    }
    event.preventDefault();
    void this.roiTrophies.dismissHighlightTask.perform();
    void this.router.transitionTo('hotel.pricing-manager', {
      queryParams: { month: this.month, view: PRIMA_VIEWS.CALENDAR },
    });
  }

  <template>
    <a
      class="prism-link inline-flex items-center gap-50"
      href={{this.url}}
      {{on "click" this.navigate}}
    ><PrismIcon
        @color="inherit"
        @name="view-calendar"
        @size="small"
      />{{this.formattedDate}}</a>
  </template>
}
